import { resolveCanvasImageSrc } from "../../../api/canvasImages.api";
import { panViewport } from "../../../features/canvas/geometry/viewportTransform";
import { readCanvasThemePalette } from "../../../features/canvas/renderer/canvasTheme";
import { createImageCache } from "../../../features/canvas/renderer/imageCache";
import { createRenderScheduler } from "../../../features/canvas/renderer/renderScheduler";
import { drawOverlayScene } from "../../../features/canvas/renderer/drawOverlay";
import { drawStaticScene } from "../../../features/canvas/renderer/drawScene";

const THEME_ATTRIBUTE_FILTER = Object.freeze(["data-theme"]);

const observeThemeChanges = (onThemeChange) => {
  if (typeof MutationObserver === "undefined") return null;

  const observer = new MutationObserver(onThemeChange);
  observer.observe(document.documentElement, {
    attributeFilter: [...THEME_ATTRIBUTE_FILTER],
    attributes: true,
  });

  return observer;
};

const getDevicePixelRatio = () => window.devicePixelRatio || 1;

const getWheelPanDelta = (event) => {
  if (event.shiftKey) return { x: -(event.deltaX || event.deltaY), y: 0 };

  return { x: -event.deltaX, y: -event.deltaY };
};

const resizeCanvasBuffers = ({ canvases, rect }) => {
  const dpr = getDevicePixelRatio();
  canvases.forEach((canvas) => {
    if (!canvas) return;
    canvas.width = Math.max(Math.round(rect.width * dpr), 1);
    canvas.height = Math.max(Math.round(rect.height * dpr), 1);
  });
};

/**
 * Connects the engine to the DOM: rAF render scheduler over the two canvas
 * layers, DPR-aware buffer sizing, wheel pan/zoom, and imperative cursor
 * updates. Returns a detach function plus screen-point helpers for pointer
 * handlers.
 * @param {{ container: HTMLElement, engine: object, overlayCanvas: HTMLCanvasElement, staticCanvas: HTMLCanvasElement }} params
 * @returns {{ detach: Function, getScreenPoint: Function, refreshRect: Function }}
 */
export const attachStageToEngine = ({
  container,
  engine,
  overlayCanvas,
  staticCanvas,
}) => {
  let containerRect = container.getBoundingClientRect();
  const refreshRect = () => {
    containerRect = container.getBoundingClientRect();
  };

  let themePalette = readCanvasThemePalette();
  const imageCache = createImageCache({
    onImageLoad: () => scheduler.request("static"),
    resolveSrc: resolveCanvasImageSrc,
  });
  const drawLayers = (dirtyLayers) => {
    const engineState = engine.getState();
    const dpr = getDevicePixelRatio();
    const getImage = imageCache.getImage;
    const palette = themePalette;
    if (dirtyLayers.static) {
      drawStaticScene({ canvas: staticCanvas, dpr, engineState, getImage, palette });
    }
    if (dirtyLayers.overlay) {
      drawOverlayScene({ canvas: overlayCanvas, dpr, engineState, getImage, palette });
    }
  };
  const scheduler = createRenderScheduler({ drawLayers });
  const themeObserver = observeThemeChanges(() => {
    themePalette = readCanvasThemePalette();
    scheduler.request("both");
  });

  const applyStageSize = () => {
    refreshRect();
    resizeCanvasBuffers({ canvases: [staticCanvas, overlayCanvas], rect: containerRect });
    engine.setSize({ height: containerRect.height, width: containerRect.width });
    scheduler.request("both");
  };

  const handleWheel = (event) => {
    event.preventDefault();
    const screenPoint = {
      x: event.clientX - containerRect.left,
      y: event.clientY - containerRect.top,
    };
    if (event.ctrlKey || event.metaKey) {
      return engine.zoomBy({ direction: event.deltaY < 0 ? 1 : -1, screenPoint });
    }
    engine.commitTextEditing();
    engine.updateViewport(
      panViewport({
        delta: getWheelPanDelta(event),
        viewport: engine.getState().viewport,
      })
    );
  };

  const resizeObserver =
    typeof ResizeObserver === "undefined" ? null : new ResizeObserver(applyStageSize);
  resizeObserver?.observe(container);
  container.addEventListener("wheel", handleWheel, { passive: false });
  window.addEventListener("resize", applyStageSize);
  window.addEventListener("scroll", refreshRect, { passive: true });
  engine.setRenderRequester(scheduler.request);
  engine.setCursorSink((cursor) => {
    container.style.cursor = cursor;
  });
  applyStageSize();

  const detach = () => {
    resizeObserver?.disconnect();
    themeObserver?.disconnect();
    container.removeEventListener("wheel", handleWheel);
    window.removeEventListener("resize", applyStageSize);
    window.removeEventListener("scroll", refreshRect);
    engine.setRenderRequester(null);
    engine.setCursorSink(null);
    scheduler.dispose();
    imageCache.dispose();
  };

  const getScreenPoint = (event) => ({
    x: event.clientX - containerRect.left,
    y: event.clientY - containerRect.top,
  });

  return { detach, getScreenPoint, refreshRect };
};
